import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ClipCard from "@/components/feature/ClipCard";
import { clips } from "@/mocks/clips";
import { formatViews } from "@/utils/format";

const SITE_URL = "https://wqxtv.cn";

type TabKey = "quotes" | "transcript" | "danmaku";

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: "quotes", label: "主播关键发言", icon: "ri-double-quotes-l" },
  { key: "transcript", label: "直播内容文字版", icon: "ri-file-text-line" },
  { key: "danmaku", label: "弹幕热议", icon: "ri-chat-3-line" },
];

export default function ClipDetail() {
  const { id } = useParams<{ id: string }>();
  const clip = clips.find((item) => item.id === id);
  const [tab, setTab] = useState<TabKey>("quotes");
  const [notice, setNotice] = useState(false);
  const [copied, setCopied] = useState(false);

  const related = useMemo(() => {
    if (!clip) return [];
    return clips
      .filter((item) => item.id !== clip.id)
      .sort((a, b) => {
        const sameCatA = a.categoryId === clip.categoryId ? 1 : 0;
        const sameCatB = b.categoryId === clip.categoryId ? 1 : 0;
        if (sameCatA !== sameCatB) return sameCatB - sameCatA;
        return b.hot - a.hot;
      })
      .slice(0, 3);
  }, [clip]);

  if (!clip) {
    return (
      <section className="mx-auto flex w-full max-w-content flex-col items-center gap-5 px-4 py-24 text-center md:px-6">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-600">
          <i className="ri-error-warning-line text-2xl" />
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">没有找到这个爆料片段</h1>
        <p className="max-w-md font-body text-sm text-foreground-600">
          片段可能已经被重新归档，或者链接不完整。回到片段库看看其他整理好的高能时刻吧。
        </p>
        <Link
          to="/clips"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" />
          返回爆料片段库
        </Link>
      </section>
    );
  }

  const canonical = `${SITE_URL}/clip/${clip.id}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: clip.title,
      description: clip.summary,
      datePublished: clip.date,
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: "爆料现场" },
      publisher: { "@type": "Organization", name: "爆料现场" },
      about: clip.tags.join("、"),
      mainEntityOfPage: canonical,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "爆料片段库", item: `${SITE_URL}/clips` },
        { "@type": "ListItem", position: 3, name: clip.title, item: canonical },
      ],
    },
  ];

  const copyTranscript = async () => {
    const text = clip.transcript.map((line) => `[${line.time}] ${line.speaker}：${line.text}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <Seo
        title={`${clip.title} | 爆料直播高能片段 | 爆料现场`}
        description={`${clip.summary} 本片段收录主播关键发言、弹幕热议与直播内容文字版。`}
        keywords={`${clip.tags.join(",")},爆料直播,直播中爆料片段,主播关键发言,直播内容文字版`}
        canonical={canonical}
        ogType="article"
        ogImage={clip.cover}
        jsonLd={jsonLd}
      />

      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "爆料片段库", to: "/clips" },
          { label: clip.title },
        ]}
      />

      <article className="mx-auto w-full max-w-content px-4 pt-10 md:px-6 md:pt-14">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/clips?cat=${clip.categoryId}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-label text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-price-tag-3-line" />
                {clip.host}
              </Link>
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-label text-primary-700">
                {clip.duration}
              </span>
              <span className="text-xs font-label text-foreground-500">归档于 {clip.date}</span>
            </div>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {clip.title}
            </h1>
            <p className="mt-4 font-body text-sm leading-relaxed text-foreground-600 md:text-base">
              {clip.summary}
            </p>
          </div>

          <div className="flex shrink-0 gap-4 rounded-lg border border-background-200 bg-background-100 px-5 py-4">
            <div className="flex flex-col">
              <span className="text-xs font-label text-foreground-500">回看量</span>
              <span className="font-heading text-lg font-bold text-foreground-950">
                {formatViews(clip.views)}
              </span>
            </div>
            <span className="w-px bg-background-200" />
            <div className="flex flex-col">
              <span className="text-xs font-label text-foreground-500">弹幕讨论</span>
              <span className="font-heading text-lg font-bold text-foreground-950">
                {formatViews(clip.comments)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1.7fr_1fr]">
          <div className="flex flex-col gap-8">
            <div className="relative h-[220px] w-full overflow-hidden rounded-lg border border-background-200 md:h-[360px]">
              <img
                src={clip.cover}
                alt={`${clip.title} 片段画面`}
                title={`${clip.title} - 爆料直播片段`}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-foreground-950/55">
                <button
                  type="button"
                  onClick={() => setNotice(true)}
                  aria-label="播放片段索引提示"
                  className="flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-105 md:h-16 md:w-16"
                >
                  <i className="ri-play-fill text-2xl" />
                </button>
                <span className="rounded-full bg-foreground-950/60 px-3 py-1 text-xs font-label text-background-50">
                  片段索引 · {clip.duration}
                </span>
              </div>
            </div>

            {notice && (
              <div className="flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-950/40">
                <i className="ri-information-line mt-0.5 text-primary-600 dark:text-primary-300" />
                <p className="flex-1 font-body text-sm leading-relaxed text-primary-800 dark:text-primary-200">
                  本片段为公开直播录播的内容索引，完整回放请以原始直播平台为准。下方已整理好
                  <strong className="mx-1 font-label">主播关键发言</strong>、<strong className="mx-1 font-label">直播内容文字版</strong>与<strong className="mx-1 font-label">弹幕热议</strong>，可逐句核对。
                </p>
                <button
                  type="button"
                  onClick={() => setNotice(false)}
                  aria-label="关闭提示"
                  className="cursor-pointer text-primary-600 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-100"
                >
                  <i className="ri-close-line" />
                </button>
              </div>
            )}

            <div>
              <div className="flex flex-wrap items-center gap-2 rounded-full border border-background-200 bg-background-100 p-1">
                {tabs.map((item) => {
                  const active = tab === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setTab(item.key)}
                      className={`inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-xs font-label transition-colors md:text-sm ${
                        active ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      <i className={item.icon} />
                      {item.label}
                    </button>
                  );
                })}
              </div>

              <div className="mt-5">
                {tab === "quotes" && (
                  <ol className="flex flex-col gap-4">
                    {clip.quotes.map((quote) => (
                      <li
                        key={quote.time}
                        className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5"
                      >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-500 font-label text-xs font-bold text-primary-950">
                          {quote.time}
                        </span>
                        <div className="flex flex-col gap-1.5">
                          <span className="font-label text-xs text-foreground-500">{quote.speaker}</span>
                          <p className="font-heading text-base font-bold leading-relaxed text-foreground-950 md:text-lg">
                            “{quote.text}”
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}

                {tab === "transcript" && (
                  <div className="rounded-lg border border-background-200 bg-background-100">
                    <div className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-3 md:px-5">
                      <span className="font-label text-sm text-foreground-600">
                        按时间顺序还原 · 共 {clip.transcript.length} 段
                      </span>
                      <button
                        type="button"
                        onClick={copyTranscript}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs font-label text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                      >
                        <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
                        {copied ? "已复制全文" : "复制文字版"}
                      </button>
                    </div>
                    <ul className="flex flex-col divide-y divide-background-200">
                      {clip.transcript.map((line) => (
                        <li key={`${line.time}-${line.speaker}`} className="flex gap-4 px-4 py-4 md:px-5">
                          <span className="w-14 shrink-0 font-label text-xs text-primary-600">{line.time}</span>
                          <div className="flex flex-col gap-1">
                            <span className="font-label text-xs text-foreground-500">{line.speaker}</span>
                            <p className="font-body text-sm leading-relaxed text-foreground-800">{line.text}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {tab === "danmaku" && (
                  <ul className="flex flex-col gap-3">
                    {clip.danmaku.map((item) => (
                      <li
                        key={item.id}
                        className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-xs font-label text-background-50">
                          {item.user.slice(0, 1)}
                        </span>
                        <div className="flex flex-col gap-1">
                          <span className="font-label text-xs text-foreground-500">{item.user}</span>
                          <p className="font-body text-sm leading-relaxed text-foreground-800">{item.text}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">片段速览</h2>
              <dl className="mt-4 flex flex-col gap-3 font-label text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">主播 / 场次</dt>
                  <dd className="text-foreground-900">{clip.host}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">片段时长</dt>
                  <dd className="text-foreground-900">{clip.duration}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">归档时间</dt>
                  <dd className="text-foreground-900">{clip.date}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">讨论热度</dt>
                  <dd className="text-primary-600">{clip.hot} / 100</dd>
                </div>
              </dl>
              <div className="mt-5 flex flex-wrap gap-2">
                {clip.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/clips?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-accent-100 px-3 py-1.5 text-xs font-label text-accent-900 transition-colors hover:bg-accent-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-secondary-50 p-5 dark:bg-secondary-950/50">
              <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐</h2>
              <ul className="mt-4 flex flex-col gap-4">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link to={`/clip/${item.id}`} className="group flex gap-3">
                      <span className="h-[56px] w-[84px] shrink-0 overflow-hidden rounded-md">
                        <img
                          src={item.cover}
                          alt={`${item.title} 相关推荐封面`}
                          title={`${item.title} - 爆料直播片段`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="flex flex-col gap-1">
                        <span className="font-heading text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="font-label text-xs text-foreground-500">
                          {item.host} · {item.duration}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </article>

      <section className="mx-auto w-full max-w-content px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col gap-2 border-b border-background-200 pb-4 md:flex-row md:items-end md:justify-between">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            还在找其他高能时刻？
          </h2>
          <Link
            to="/clips"
            className="inline-flex w-fit items-center gap-1 whitespace-nowrap text-sm font-label text-primary-600 transition-colors hover:text-primary-700"
          >
            浏览全部片段
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((item) => (
            <ClipCard key={`bottom-${item.id}`} clip={item} />
          ))}
        </div>
      </section>
    </>
  );
}